import { Injectable } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { LanguageServices } from 'src/app/services/LanguageServices';
import { BuilderScreenBetaService } from './builder-screen.service';
import { BuilderNode, BuilderComponentType } from '../interfaces/builder-screen.models';

/**
 * Interface para resultado de validação de formulário
 */
export interface FormValidationResult {
  valid: boolean;
  errors: FormValidationError[];
}

/**
 * Interface para erro de validação de campo
 */
export interface FormValidationError {
  fieldId: string;
  fieldLabel: string;
  dataPath: string;
  message: string;
}

/**
 * Serviço de validação de formulário
 * Valida campos obrigatórios antes de executar ações (como fluxos)
 */
@Injectable({
  providedIn: 'root'
})
export class FormValidationService {

  // Tipos de campos que podem ser validados como obrigatórios
  private readonly FORM_FIELD_TYPES: BuilderComponentType[] = [
    'text', 'textarea', 'number', 'switch', 'date', 
    'select', 'dropdown', 'querySelect', 'keyValue', 
    'keyValueObject', 'tag', 'search'
  ];

  constructor(
    private message: NzMessageService,
    private ls: LanguageServices
  ) {}

  /**
   * Valida campos obrigatórios antes de executar uma ação
   * @param nodes Lista de nós do builder
   * @param formData Dados do formulário (usado como fallback)
   * @returns Resultado da validação
   */
  validateRequiredFields(
    nodes: ReadonlyArray<BuilderNode>,
    formData: any,
    selectedItem?: any
  ): FormValidationResult {
    const errors: FormValidationError[] = [];

    const requiredFields = nodes.filter(n => 
      this.isFormField(n.type) && 
      (n.props as any).required === true
    );

    for (const field of requiredFields) {
      const props = field.props as any;
      const dataPath = props.dataPath || props.field || field.id;
      
      // 1. Tenta obter o valor do DOM (valor visual atual)
      let value = this.getValueFromDOM(field.id);
      
      // 2. Se não encontrar no DOM, tenta formData na raiz
      if (value === undefined) {
        value = this.getValueByPath(formData, dataPath);
      }

      // 3. Se ainda não encontrou e há um item selecionado, verifica nele.
      //    Campos de master-detail têm dataPath relativo ao item do array
      //    (ex: "CODECOMPANY" existe em response.magalu[0], não na raiz).
      if ((value === undefined || value === null) && selectedItem) {
        value = this.getValueByPath(selectedItem, dataPath);
      }

      if (this.isEmpty(value)) {
        errors.push({
          fieldId: field.id,
          fieldLabel: props.label || dataPath,
          dataPath: dataPath,
          message: this.ls.instant('builderScreen.msg.validation.fieldRequired', { field: props.label || dataPath })
        });
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
  
  /**
   * Obtém o valor atual de um campo diretamente do DOM
   * @param fieldId ID do campo no builder
   * @returns Valor do campo ou undefined se não encontrar
   */
  private getValueFromDOM(fieldId: string): any {
    const container = document.querySelector(`[data-component-id="${fieldId}"]`);
    if (!container) {
      return undefined;
    }

    // nz-select (search/dropdown) DEVE ser verificado ANTES de input genérico,
    // porque nz-select contém um <input> interno de busca cujo valor é o texto
    // digitado pelo usuário (fica vazio após selecionar um item).
    const select = container.querySelector('nz-select');
    if (select) {
      const selectedItem = select.querySelector('.ant-select-selection-item');
      if (selectedItem && selectedItem.textContent?.trim()) {
        return selectedItem.textContent.trim();
      }
      // Sem item selecionado visível — retorna undefined para permitir fallback ao formData
      return undefined;
    }

    // nz-input-number também ANTES do input genérico (contém input interno)
    const inputNumber = container.querySelector('nz-input-number input') as HTMLInputElement;
    if (inputNumber) {
      return inputNumber.value;
    }

    // nz-date-picker também ANTES do input genérico
    const datePicker = container.querySelector('nz-date-picker input') as HTMLInputElement;
    if (datePicker) {
      return datePicker.value;
    }

    // Input genérico (text, number nativo, etc.)
    const input = container.querySelector('input:not([type="checkbox"]):not([type="radio"])') as HTMLInputElement;
    if (input) {
      return input.value;
    }

    const textarea = container.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea) {
      return textarea.value;
    }

    // Switch (checkbox)
    const checkbox = container.querySelector('input[type="checkbox"]') as HTMLInputElement;
    if (checkbox) {
      return checkbox.checked;
    }

    return undefined;
  }

  /**
   * Exibe mensagens de erro de validação
   * @param errors Lista de erros de validação
   */
  showValidationErrors(errors: FormValidationError[]): void {
    if (errors.length === 0) {
      return;
    }

    // Exibir cada erro como uma mensagem separada
    errors.forEach(error => {
      this.message.error(error.message);
    });

    // Se houver muitos erros, exibir resumo
    if (errors.length > 3) {
      this.message.warning(this.ls.instant('builderScreen.msg.validation.requiredCount', { count: errors.length }));
    }
  }

  /**
   * Verifica se o tipo é um campo de formulário
   */
  private isFormField(type: BuilderComponentType): boolean {
    return this.FORM_FIELD_TYPES.includes(type);
  }

  /**
   * Verifica se um valor está vazio
   */
  private isEmpty(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim() === '';
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }

    return false;
  }

  /**
   * Obtém valor de um objeto usando um path (ex: "customer.name")
   */
  private getValueByPath(obj: any, path: string): any {
    if (!obj || !path) {
      return undefined;
    }

    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
      if (current === null || current === undefined) {
        return undefined;
      }
      current = current[key];
    }

    return current;
  }

  /**
   * Retorna lista de campos obrigatórios não preenchidos
   * Útil para highlight visual
   */
  getInvalidFieldIds(nodes: ReadonlyArray<BuilderNode>, formData: any): string[] {
    const result = this.validateRequiredFields(nodes, formData);
    return result.errors.map(e => e.fieldId);
  }
}
